import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Hash,
  User,
  Phone,
  Calendar,
  Coins,
  RotateCcw,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { AgentHeader } from '../../components/layout/AgentHeader';
import { AgentSidebar } from '../../components/layout/AgentSidebar';
import { BottomNav } from '../../components/layout/BottomNav';
import { Modal } from '../../components/common/Modal';
import { cashTransactionsApi } from '../../api/cash-transactions.api';
import { TransactionType, TransactionStatus } from '../../types/cash-transaction.types';
import { clsx } from 'clsx';

const statusConfig: Record<string, { icon: any; color: string; bg: string; border: string; label: string }> = {
  [TransactionStatus.PENDING]: { icon: Clock, color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'Pendiente' },
  [TransactionStatus.CONFIRMED]: { icon: CheckCircle, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Confirmada' },
  [TransactionStatus.COMPLETED]: { icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200', label: 'Completada' },
  [TransactionStatus.CANCELLED]: { icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200', label: 'Cancelada' },
  [TransactionStatus.FAILED]: { icon: XCircle, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: 'Fallida' },
  REVERSED: { icon: AlertCircle, color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Revertida' },
};

const TransactionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showReversal, setShowReversal] = useState(false);
  const [reversalReason, setReversalReason] = useState('');

  const { data: transaction, isLoading, error } = useQuery({
    queryKey: ['transaction', id],
    queryFn: () => cashTransactionsApi.getById(id!),
    enabled: !!id,
  });

  const reversalMutation = useMutation({
    mutationFn: () => cashTransactionsApi.requestReversal(id!, reversalReason),
    onSuccess: (data) => {
      toast.success(data.message);
      setShowReversal(false);
      setReversalReason('');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Error al solicitar reversión');
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AgentHeader />
        <div className="flex">
          <AgentSidebar />
          <main className="flex-1 p-6">
            <div className="max-w-2xl mx-auto space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 h-20 animate-pulse" />
              ))}
            </div>
          </main>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AgentHeader />
        <div className="flex">
          <AgentSidebar />
          <main className="flex-1 p-6">
            <div className="max-w-2xl mx-auto">
              <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
                <ArrowLeft size={18} /> Volver
              </button>
              <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-gray-700 font-medium">Transacción no encontrada</p>
              </div>
            </div>
          </main>
        </div>
        <BottomNav />
      </div>
    );
  }

  const isCashIn = transaction.type === TransactionType.CASH_IN;
  const status = statusConfig[transaction.status] ?? statusConfig[TransactionStatus.PENDING];
  const StatusIcon = status.icon;
  const canRequestReversal = transaction.status === TransactionStatus.COMPLETED;

  return (
    <div className="min-h-screen bg-gray-50">
      <AgentHeader />

      <div className="flex">
        <AgentSidebar />

        <main className="flex-1 p-4 lg:p-6 pb-24 lg:pb-6">
          <div className="max-w-2xl mx-auto space-y-5">

            {/* Back */}
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
              <ArrowLeft size={16} /> Volver a transacciones
            </button>

            {/* Hero card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-center">
              <div className={clsx('w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4', isCashIn ? 'bg-green-100' : 'bg-red-100')}>
                {isCashIn
                  ? <ArrowDownToLine className="text-green-600" size={32} />
                  : <ArrowUpFromLine className="text-red-600" size={32} />}
              </div>
              <p className="text-sm text-gray-500 mb-1">{isCashIn ? 'Depósito de efectivo' : 'Retiro de efectivo'}</p>
              <p className={clsx('text-4xl font-bold mb-3', isCashIn ? 'text-green-600' : 'text-red-600')}>
                {isCashIn ? '+' : '−'}{transaction.amount.toLocaleString()} XAF
              </p>
              <span className={clsx('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border', status.bg, status.color, status.border)}>
                <StatusIcon size={14} />
                {status.label}
              </span>
            </div>

            {/* Details */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
              <div className="px-6 py-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Información</h3>
              </div>

              <div className="px-6 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-2"><Hash size={15} /> Referencia</span>
                <span className="text-sm font-mono font-semibold text-gray-900">{transaction.reference}</span>
              </div>

              <div className="px-6 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-2"><User size={15} /> Cliente</span>
                <span className="text-sm font-semibold text-gray-900">{transaction.customerName}</span>
              </div>

              <div className="px-6 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-2"><Phone size={15} /> Teléfono</span>
                <span className="text-sm text-gray-700">{transaction.customerPhone}</span>
              </div>

              <div className="px-6 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-2"><Calendar size={15} /> Creada</span>
                <span className="text-sm text-gray-700">{format(new Date(transaction.createdAt), "d 'de' MMM yyyy, HH:mm:ss", { locale: es })}</span>
              </div>

              {transaction.completedAt && (
                <div className="px-6 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-500 flex items-center gap-2"><CheckCircle size={15} /> Completada</span>
                  <span className="text-sm text-gray-700">{format(new Date(transaction.completedAt), "d 'de' MMM yyyy, HH:mm:ss", { locale: es })}</span>
                </div>
              )}
            </div>

            {/* Commission */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
              <div className="px-6 py-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Comisión</h3>
              </div>
              <div className="px-6 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-2"><Coins size={15} /> Tasa aplicada</span>
                <span className="text-sm font-semibold text-gray-700">{transaction.commissionRate}%</span>
              </div>
              <div className="px-6 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-2"><Coins size={15} /> Comisión ganada</span>
                <span className="text-sm font-bold text-orange-600">+{transaction.commissionAmount.toLocaleString()} XAF</span>
              </div>
            </div>

            {/* Reversal */}
            {canRequestReversal && (
              <button
                onClick={() => setShowReversal(true)}
                className="w-full flex items-center justify-center gap-2 py-3 border border-orange-300 text-orange-600 text-sm font-medium rounded-xl hover:bg-orange-50 transition-colors"
              >
                <RotateCcw size={16} />
                Solicitar revisión / reversión
              </button>
            )}
          </div>
        </main>
      </div>

      <BottomNav />

      {/* Reversal Modal */}
      <Modal isOpen={showReversal} onClose={() => setShowReversal(false)} title="Solicitar Revisión" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Describe el motivo por el que solicitas la revisión de esta transacción.
            Un administrador revisará tu solicitud.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
            <textarea
              rows={4}
              value={reversalReason}
              onChange={(e) => setReversalReason(e.target.value)}
              placeholder="Ej: El cliente no recibió el efectivo..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowReversal(false)}
              className="flex-1 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => reversalMutation.mutate()}
              disabled={reversalMutation.isPending || reversalReason.trim().length < 10}
              className="flex-1 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {reversalMutation.isPending ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Enviando...</>
              ) : (
                <>Enviar solicitud</>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TransactionDetailPage;

